import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "./AuctionPayment.scss";

export default function AuctionPayment() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [winningBid, setWinningBid] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuctionAndBid();
  }, [auctionId]);

  const fetchAuctionAndBid = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
      }

      // Fetch bids to get the winning bid
      const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
      if (bidsResponse && bidsResponse.success && bidsResponse.data) {
        const bids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
        // Find the winning bid (bid with status 'won' or matching winning_bid_id)
        const bid = bids.find(b => 
          b.bid_id === auctionResponse.data.winning_bid_id || 
          b.bid_status === 'won'
        );
        if (bid) {
          setWinningBid(bid);
        }
      }
    } catch (error) {
      console.error("Error fetching auction and bid:", error);
      toast.error("Failed to load auction details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const handleProceedToPayment = () => {
    // Navigate to party connection page
    navigate(`/auction/${auctionId}/party-connection`);
  };

  const handleReviewLater = () => {
    navigate("/auction/my/bids");
  };

  if (loading) {
    return <div className="auction-payment-page loading">Loading...</div>;
  }

  if (!auction || !winningBid) {
    return <div className="auction-payment-page error">Auction or winning bid not found</div>;
  }

  return (
    <div className="auction-payment-page">
      <div className="payment-overlay">
        <div className="payment-card">
          {/* Close Button */}
          <button className="close-btn" onClick={handleReviewLater}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          {/* Header Section */}
          <div className="header-section">
            <div className="trophy-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                <path d="M4 22h16"></path>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
              </svg>
            </div>
            <h1 className="congratulations-title">Congratulations!</h1>
            <h2 className="won-title">You Won the Auction!</h2>
            <p className="congratulations-description">
              Your bid has been accepted by the seller. Complete the payment to proceed.
            </p>
          </div>

          {/* Winning Auction Details Section */}
          <div className="winning-details-section">
            <div className="section-header">
              <div className="section-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="3" x2="9" y2="21"></line>
                  <line x1="15" y1="3" x2="15" y2="21"></line>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="3" y1="15" x2="21" y2="15"></line>
                </svg>
              </div>
              <h3 className="section-title">Winning Auction Details</h3>
            </div>
            <div className="item-name">{auction.product_name}</div>
            <div className="item-description">
              {auction.product_description || "No description available"}
            </div>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">Category:</span>
                <span className="detail-value">{auction.category_name || "N/A"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Condition:</span>
                <span className="detail-value">Excellent</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Location:</span>
                <span className="detail-value">{auction.city || auction.country || "India"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Quantity:</span>
                <span className="detail-value">{auction.quantity || 1} Unit{auction.quantity > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          {/* Your Winning Bid Section */}
          <div className="winning-bid-section">
            <div className="winning-bid-header">
              <div className="bid-icon-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div className="bid-text">
                <div className="bid-label">Your Winning Bid</div>
                <div className="bid-congratulations">Congratulations on securing this item!</div>
              </div>
              <div className="bid-amount-wrapper">
                <div className="bid-amount">{formatCurrency(winningBid.bid_amount || winningBid.amount)}</div>
                <div className="bid-taxes">+ applicable taxes</div>
              </div>
            </div>
          </div>

          {/* Next Steps Section */}
          <div className="next-steps-section">
            <h3 className="section-title">Next Steps</h3>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </div>
                <div className="step-content">
                  <div className="step-title">Contact Exchange</div>
                  <div className="step-description">
                    Seller's contact details will be shared with you for coordination
                  </div>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </div>
                <div className="step-content">
                  <div className="step-title">Proforma Invoice</div>
                  <div className="step-description">
                    You will receive a detailed proforma invoice with payment terms
                  </div>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <div className="step-title">Inspection (Optional)</div>
                  <div className="step-description">
                    Schedule inspection if required before final payment
                  </div>
                </div>
              </div>

              <div className="step-item step-complete">
                <div className="step-number">4</div>
                <div className="step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <div className="step-content">
                  <div className="step-title">Complete Payment</div>
                  <div className="step-description">
                    Make secure payment via bank transfer or escrow service
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important SLA Section */}
          <div className="sla-section">
            <div className="sla-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="sla-content">
              <div className="sla-title">Important: 72-Hour SLA Window</div>
              <div className="sla-description">
                Please complete the party connection and inspection process within 72 hours. The payment must be completed within the specified timeframe to secure your purchase.
              </div>
            </div>
          </div>

          {/* Seller Information Section */}
          <div className="seller-info-section">
            <h3 className="section-title">Seller Information</h3>
            <div className="seller-name">
              Seller: {auction.seller_company || auction.seller_name || "N/A"} - {auction.city || ""}
            </div>
            <div className="seller-note">
              Contact details will be shared after you proceed to payment
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="btn-review-later" onClick={handleReviewLater}>
              Review Later
            </button>
            <button className="btn-proceed-payment" onClick={handleProceedToPayment}>
              Proceed to Payment →
            </button>
          </div>

          {/* Footer Note */}
          <div className="footer-note">
            By proceeding, you agree to complete the purchase at the winning bid amount
          </div>
        </div>
      </div>
    </div>
  );
}
